import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import DetailsHeaderBar from "../components/DetailsHeaderBar";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { categoryService } from "../services/categoryService";

function EditCategory() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const [status, setStatus] = useState(statusOptions[0]);

  useEffect(() => {
    fetchCategoryDetails();
  }, [id]);

  const fetchCategoryDetails = async () => {
    try {
      setFetching(true);
      const response = await categoryService.getCategory(id);
      const category = response.category || response.data?.category || response;

      console.log("Fetched category:", category);

      // Store original data for comparison
      setOriginalData({
        name: category.name || "",
        slug: category.slug || "",
        description: category.description || "",
        status: category.status || "active",
        image_url: category.image_url || category.image || "",
        image: category.image || "",
      });

      // Set form values from API response
      setCategoryName(category.name || "");
      setSlug(category.slug || "");
      setDescription(category.description || "");

      // Set status
      if (category.status) {
        setStatus(
          statusOptions.find((opt) => opt.value === category.status) ||
            statusOptions[0],
        );
      }

      // Set image if exists
      if (category.image_url || category.image) {
        const imageUrl = category.image_url || category.image;
        setImages([
          {
            preview: imageUrl,
            file: null,
            isExisting: true,
            originalUrl: imageUrl,
            fileName: imageUrl.split('/').pop() || 'category-image.jpg'
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch category details:", error);
      alert(error.response?.data?.message || "Failed to load category details");
      navigate("/manage-category");
    } finally {
      setFetching(false);
    }
  };

  const validateBasicDetails = () => {
    if (!categoryName.trim()) {
      alert("Category name is required");
      return false;
    }
    return true;
  };

  const validateStep = () => {
    if (step === 1) return true;
    if (step === 2) {
      return categoryName.trim() !== "";
    }
    return true;
  };

  const nextStep = () => {
    if (step === 2) {
      const isValid = validateBasicDetails();
      if (!isValid) return;
    }
    if (validateStep()) setStep((prev) => Math.min(prev + 1, 2));
    else alert("Please fill all required fields before proceeding.");
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const steps = [
    { label: "Category image", sub: "Enter category image details" },
    { label: "Basic details", sub: "Enter category basic details" },
  ];

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
      fileName: file.name
    }));
    setImages(newImages);
  };

  // Remove image
  const removeImage = () => {
    setImages([]);
  };

  // Check if data has changed from original
  const hasDataChanged = () => {
    if (!originalData) return false;

    // Check for image changes
    const hasImageChanged = images.length === 0 && originalData.image_url ? true : 
        images.length > 0 && images[0].isExisting === false;

    return (
      categoryName !== originalData.name ||
      slug !== originalData.slug ||
      description !== originalData.description ||
      status.value !== originalData.status ||
      hasImageChanged
    );
  };

  const handleUpdateCategory = async () => {
    if (!validateStep()) {
      alert("Please fill all required fields before updating the category.");
      return;
    }

    // Check if any changes were made
    if (!hasDataChanged()) {
      alert(
        "No changes detected. Please modify at least one field before updating.",
      );
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // Always append all required fields
      formData.append("name", categoryName);
      formData.append("slug", (slug || categoryName).toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
      formData.append("description", description || "");
      formData.append("status", status.value);

      // Handle image properly
      if (images.length > 0) {
        if (images[0].file) {
          // New image uploaded
          formData.append("image", images[0].file);
          console.log("Appending new image file:", images[0].fileName);
        }
        // If isExisting is true, don't append image (backend will keep existing)
      } else if (images.length === 0 && originalData.image) {
        // Image was removed - send empty string
        formData.append("image", "");
        console.log("Removing existing image");
      }

      console.log("FormData contents before sending:");
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, key === 'image' ? `${value.name || 'file'} (${value.type || 'no type'})` : value);
      }

      // Debug: Check what we're about to send
      console.log("Sending update with:", {
        name: categoryName,
        slug: (slug || categoryName).toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
        description,
        status: status.value,
        hasImage: images.length > 0,
        isNewImage: images.length > 0 && images[0].isExisting === false
      });

      const response = await categoryService.updateCategory(id, formData);
      console.log("Update response:", response);

      setOpenSnackbar(true);
      setTimeout(() => {
        navigate(`/category-details/${id}`);
      }, 2000);
    } catch (error) {
      console.error("Update category error:", error);
      console.error("Error response:", error.response?.data);
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.values(errors).flat().join('\n');
        alert(`Validation errors:\n${errorMessages}`);
      } else {
        alert(
          error.response?.data?.message ||
            error.message ||
            "Failed to update category",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  if (fetching) {
    return (
      <div className="manage-member-layout">
        <SideBar />
        <div className="product-details-main">
          <DetailsHeaderBar
            title="Edit Category"
            subtitle="You can edit the category details to update the category details"
            breadcrumbs={[
              { label: "Home", path: "/manage-member" },
              { label: "Manage Category", path: "/manage-category" },
              { label: "Edit Category" },
            ]}
          />
          <div style={{ textAlign: "center", padding: "50px" }}>
            Loading category details...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="product-details-main">
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            severity="success"
            icon={
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "rgba(51, 178, 51, 1",
                }}
              >
                ✔
              </span>
            }
            sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "25px",
              width: "100%",
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            Category updated successfully!
          </MuiAlert>
        </Snackbar>

        <DetailsHeaderBar
          title="Edit Category"
          subtitle="You can edit the category details to update the category details"
          breadcrumbs={[
            { label: "Home", path: "/manage-member" },
            { label: "Manage Category", path: "/manage-category" },
            { label: "Edit Category" },
          ]}
        />

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <div
                    className={`circle ${isCompleted ? "completed" : isActive ? "active" : ""}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </div>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${isCompleted ? "line-filled" : "line-empty"}`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="step-content">
            {step === 1 && (
              <div>
                <h3>Category Image Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Category Image <span className="required"></span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImagesChange}
                      style={{ maxWidth: "100%" }}
                      disabled={loading}
                    />
                    <small>
                      {images.length > 0 && images[0].isExisting 
                        ? "Current image will be kept. Upload a new image to replace it."
                        : "Upload a new image to replace the existing one"}
                    </small>

                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                      {images.map((img, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            height: 120,
                          }}
                        >
                          <img
                            src={img.preview}
                            alt="category"
                            onClick={() => setPreviewImage(img.preview)}
                            style={{
                              width: 120,
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 6,
                              display: "block",
                              marginBottom: 6,
                              cursor: "pointer",
                            }}
                          />
                          <button
                            type="button"
                            onClick={removeImage}
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              background: "#e53935",
                              color: "#fff",
                              border: "none",
                              borderRadius: "50%",
                              width: 24,
                              height: 24,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: 14,
                            }}
                          >
                            x
                          </button>
                        </div>
                      ))}
                      {images.length === 0 && originalData?.image_url && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ color: '#666' }}>
                            Current image will be kept. Click "Choose File" to replace.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div>
                <h3>Basic Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Category name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      placeholder="Enter category name"
                      disabled={loading}
                      required
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>
                      Category slug
                    </label>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="Auto-generated from name"
                      disabled={loading}
                     maxLength={255} />
                  </div>

                  <div className="form-group">
                    <label>
                      Status <span className="required">*</span>
                    </label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select status"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      required
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: "span 2" }}>
                    <label>Description</label>
                    <textarea
                      className="note-textarea"
                      rows="4"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter category description"
                      style={{ padding: "10px" }}
                      disabled={loading}
                     maxLength={65535} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step > 1 && (
              <button
                className="btn prev"
                onClick={prevStep}
                disabled={loading}
              >
                Previous
              </button>
            )}
            {step < 2 && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={!validateStep() || loading}
                style={{
                  opacity: validateStep() ? 1 : 0.6,
                  cursor: validateStep() ? "pointer" : "not-allowed",
                }}
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button
                className="btn next"
                onClick={handleUpdateCategory}
                disabled={!validateStep() || loading || !hasDataChanged()}
                style={{
                  opacity: validateStep() && hasDataChanged() ? 1 : 0.6,
                  cursor:
                    validateStep() && hasDataChanged()
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                {loading ? "Updating..." : "Update Category"}
              </button>
            )}
          </div>

          {previewImage && (
            <div
              onClick={() => setPreviewImage(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              <div style={{ position: "relative" }}>
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    borderRadius: 10,
                  }}
                />
                <button
                  onClick={() => setPreviewImage(null)}
                  style={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    background: "#e53935",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    fontSize: 16,
                    fontWeight: "bold",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditCategory;
